import { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  SearchInput,
  Button,
  Table,
  Plus,
  Export,
  Edit,
  DropdownButton,
} from 'components';

import {
  Controls,
  ControlsSearch,
  ControlsButtons,
} from '../TableTabbedStyles';
import { useTable } from '../hooks';
import { Container } from 'components/TimeWindow';
import { BenchmarkScreen, benchmarkScreen } from 'hooks';
import { useHistory } from 'react-router-dom';
import {
  KEY as pageIndexSearchParamKey,
  usePageSearchParam,
} from 'hooks/usePageURLSearchParam/usePageURLSearchParam';
import {
  KEY as pageSizeSearchParamKey,
  usePageSizeSearchParam,
} from 'hooks/usePageSizeURLSearchParam/usePageSizeURLSearchParam';
import {
  DEFAULT_TABLE_PAGESIZE,
  DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE,
} from 'components/Table/contants';
import { Column } from 'types/Table';

type Props = {
  columns: Array<Column>;
  data: Array<any>;
  benchmarkName?: string;
  isLoading?: boolean;
};

const timeWindowOptions = [
  {
    title: 'Edit',
    value: BenchmarkScreen.editTimeWindows,
    divider: false,
    icon: <Edit size='12px' fill='#0076C0' />,
  },
  {
    title: 'Add',
    value: BenchmarkScreen.addTimeWindow,
    divider: false,
    icon: <Plus size='12px' fill='#0076C0' />,
  },
];

export const TimeWindowsTable = ({
  columns,
  data,
  benchmarkName,
  isLoading = false,
}: Props) => {
  const propsTable = useTable({
    columns,
    data,
  });
  const history = useHistory();
  const { pageIndex } = usePageSearchParam();
  const { pageSize } = usePageSizeSearchParam();
  const [search, setSearch] = useState('');

  const handleTimeWindow = (value: string) => {
    history.replace({
      search: new URLSearchParams({
        [benchmarkScreen]: value,
        [pageIndexSearchParamKey]: `${pageIndex + 1 || 1}`,
        [pageSizeSearchParamKey]: `${pageSize || DEFAULT_TABLE_PAGESIZE}`,
      }).toString(),
    });
  };

  const exportData = useMemo(
    () =>
      data.map((item: any) => {
        return columns.reduce((res: any, elem: any) => {
          const value = item[`${elem.accessor}`];

          return {
            ...res,
            [`${elem.Header}`]: value === '-' ? '' : value,
          };
        }, {});
      }),
    [data, columns]
  );

  const filteredData = useMemo(() => {
    const input = search?.trim().toLocaleLowerCase();
    if (input.length > 0) {
      return data.filter((element: any) => {
        let match = false;
        for (let column of columns) {
          const accessor = column.accessor?.toString() ?? '';
          const value = element?.[accessor];
          if (
            typeof value === 'string' &&
            value?.toLocaleLowerCase()?.includes(input)
          ) {
            match = true;
            break;
          }
        }
        return match;
      });
    }

    return data;
  }, [data, search, columns]);

  return (
    <>
      <Controls>
        <ControlsSearch>
          <SearchInput placeholder='Search' onInput={setSearch} />
        </ControlsSearch>
        <ControlsButtons>
          <CSVLink data={exportData} filename={`${benchmarkName}.csv`}>
            <Button variant='secondary'>
              <Export size='12px' />
              Export Table
            </Button>
          </CSVLink>
          <DropdownButton
            variant='primary'
            list={timeWindowOptions}
            onSelect={(data) => handleTimeWindow(data.value?.toString())}
          >
            Time Window
          </DropdownButton>
        </ControlsButtons>
      </Controls>
      <Container style={{ overflowY: 'visible' }}>
        <Table
          isLoading={isLoading}
          data={filteredData}
          columns={propsTable.fullColumns}
          noResultsMessage={DEFAULT_TABLE_SEARCH_NO_DATA_MESSAGE}
        />
      </Container>
    </>
  );
};
